import React, { type ReactNode, useLayoutEffect } from 'react';

import { useIntl } from 'react-intl-next';

import EmptyState from '@atlaskit/empty-state';

import { useAnalyticsEvents } from '../../common/analytics/generated/use-analytics-events';
import { useMessagesForLocation } from '../../common/utils/use-messages-for-location';

import { GenericErrorIcon } from './icon';

export function ErrorBoundaryUI({
	disableAnalytics,
	errorFallbackUI,
	errorActionUI,
	location,
}: ErrorBoundaryUIProps) {
	const intl = useIntl();
	const { fireEvent } = useAnalyticsEvents();
	const { header, description } = useMessagesForLocation(location);

	useLayoutEffect(() => {
		if (!disableAnalytics) {
			fireEvent('ui.errorBoundary.viewed.errorBoundaryUI', {
				location,
				isFallbackUI: !!errorFallbackUI,
			});
		}
	}, [disableAnalytics, errorFallbackUI, fireEvent, location]);

	// If a custom fallback UI is provided, render it directly at risk of consumer
	if (errorFallbackUI) {
		return errorFallbackUI;
	}

	return (
		<EmptyState
			testId="navigation-error-boundary-ui"
			header={intl.formatMessage(header)}
			description={intl.formatMessage(description)}
			headingLevel={4}
			headingSize="medium"
			primaryAction={errorActionUI}
			renderImage={() => <GenericErrorIcon />}
		/>
	);
}

type ErrorBoundaryUIProps = {
	disableAnalytics?: boolean;
	errorActionUI?: ReactNode;
	errorFallbackUI?: ReactNode;
	location: string;
};
